import { useMemo } from 'react'

import { DatasetFreshnessState, getOldestDegradedDataset } from '@/domain/states/DatasetFreshnessState'

import { NetworkStatusBar } from '../components/NetworkStatusBar'

import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'
import { useI18n } from '@/presentation/hooks/useI18n'
import { useNetworkQuality } from '@/presentation/queries/useNetworkQuality'

/**
 * Aviso global de conexão e de dado aeronáutico vencido.
 *
 * Duas coisas que antes eram invisíveis:
 *
 * 1. **retry em andamento** — o usuário via tela vazia ou erro definitivo e concluía que a internet
 *    dele estava ruim, quando o app já estava tentando de novo;
 * 2. **dado além do TTL rígido em uso** — sem este aviso, o app mostraria dado de voo vencido como se
 *    fosse atual. É por isso que o ramo "usar cópia vencida" do `DatasetCache` só pôde ser ligado
 *    junto com este componente.
 *
 * A prioridade é: dado vencido > offline > instável. Dado aeronáutico vencido é o aviso mais grave, e
 * continua valendo mesmo depois de a conexão voltar (o dado segue velho até a próxima revalidação).
 */
function NetworkStatusPresenter() {
  // A data sai do I18n, e nao de um `format` do date-fns: sem locale, o `MMM` sai sempre em ingles
  // ("20/Oct"), destoando do "20/OUT/2026" do resto do app (PLA-372).
  const { t, getStandardizedDateTimeUTC } = useI18n()
  const quality = useNetworkQuality()
  const freshness = useBehaviorSubject(DatasetFreshnessState)

  const degraded = useMemo(() => getOldestDegradedDataset(freshness), [freshness])

  if (degraded) {
    return (
      <NetworkStatusBar
        variant="error"
        message={t('NETWORK-STATUS_STALE-DATASET', { time: `${getStandardizedDateTimeUTC(degraded.loadedAt, true)}Z` })}
      />
    )
  }

  if (quality === 'offline') {
    return <NetworkStatusBar variant="error" message={t('NETWORK-STATUS_OFFLINE')} />
  }

  if (quality === 'unstable') {
    return <NetworkStatusBar variant="warning" message={t('NETWORK-STATUS_UNSTABLE')} />
  }

  return null
}

export { NetworkStatusPresenter }
